import { AbortControllerManager } from '../abortControllerManager';
import { authClient, kioskClient, publicClient } from '../client';
import {
  ICheckEmailResponse,
  ICreatePersonalInviteRequest,
  TEmail,
  IGetTokenResponse,
  IResetPasswordRequest,
  IVerifyResetCodeRequest,
  IInviteExternalUserRequest,
  TGetTokenRequest,
  TTemplateValidateInviteResponse,
  TKioskTokenRequest,
  TUserTokenRequest,
  TCreatePersonalInviteResponse,
  TCheckEmailRequest,
} from '@/types';

class AuthApi extends AbortControllerManager {
  private readonly AUTH_URL = '/v2/auth';

  constructor() {
    super();
  }

  checkEmail(body: TCheckEmailRequest): Promise<ICheckEmailResponse> {
    const signal = this.createAbortController('checkEmail');
    return authClient.post(`${this.AUTH_URL}/check-email`, body, { signal });
  }

  getAuth(body: TGetTokenRequest): Promise<IGetTokenResponse> {
    const signal = this.createAbortController('getAuth');
    return publicClient.post(`${this.AUTH_URL}/token`, body, { signal });
  }

  getAuthAuthenticated(body: TGetTokenRequest): Promise<IGetTokenResponse> {
    const signal = this.createAbortController('getAuthAuthenticated');
    return authClient.post(`${this.AUTH_URL}/token`, body, { signal });
  }

  postAuthInKiosk(body: TUserTokenRequest): Promise<IGetTokenResponse> {
    const signal = this.createAbortController('getAuthInKiosk');
    return kioskClient.post(`${this.AUTH_URL}/token`, body, { signal });
  }

  postAuthKiosk(body: TKioskTokenRequest): Promise<IGetTokenResponse> {
    const signal = this.createAbortController('postAuthKiosk');
    return publicClient.post(`${this.AUTH_URL}/token`, body, { signal });
  }

  forgotPassword(email: TEmail) {
    const signal = this.createAbortController('forgotPassword');
    return authClient.post(
      `${this.AUTH_URL}/forgot-password`,
      { email },
      { signal },
    );
  }

  forgotPasswordInKiosk(email: TEmail) {
    const signal = this.createAbortController('forgotPassword');
    return kioskClient.post(
      `${this.AUTH_URL}/forgot-password`,
      { email },
      { signal },
    );
  }

  verifyResetCode(body: IVerifyResetCodeRequest) {
    const signal = this.createAbortController('verifyResetCode');
    return authClient.post(`${this.AUTH_URL}/verify-reset-code`, body, {
      signal,
    });
  }

  resetPassword(body: IResetPasswordRequest) {
    const signal = this.createAbortController('resetPassword');
    return authClient.post(`${this.AUTH_URL}/reset-password`, body, { signal });
  }

  validateInvite(code: string): Promise<TTemplateValidateInviteResponse> {
    const signal = this.createAbortController('validateInvite');
    return authClient.post(
      `${this.AUTH_URL}/signup/validate-invite`,
      { code },
      { signal },
    );
  }

  createPersonalInviteByKiosk(
    body: ICreatePersonalInviteRequest,
  ): Promise<TCreatePersonalInviteResponse> {
    const signal = this.createAbortController('createPersonalInviteByKiosk');
    return kioskClient.post(
      `${this.AUTH_URL}/signup/create-personal-invite`,
      body,
      {
        signal,
      },
    );
  }

  createPersonalInvite(
    body: ICreatePersonalInviteRequest,
  ): Promise<TCreatePersonalInviteResponse> {
    const signal = this.createAbortController('createPersonalInvite');
    return authClient.post(
      `${this.AUTH_URL}/signup/create-personal-invite`,
      body,
      {
        signal,
      },
    );
  }

  signup(code: string, password: string) {
    const signal = this.createAbortController('signup');
    return authClient.post(
      `${this.AUTH_URL}/signup`,
      { code, password },
      { signal },
    );
  }

  inviteGuest(payload: IInviteExternalUserRequest) {
    const signal = this.createAbortController('invite-guest');
    return authClient.post(`${this.AUTH_URL}/invite-guest`, payload, {
      signal,
    });
  }

  logout() {
    const signal = this.createAbortController('logout');
    return authClient.post(`${this.AUTH_URL}/logout`, undefined, { signal });
  }
}

export const authApi = new AuthApi();
